import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Flame, Building, TreePine, Target, Layers } from 'lucide-react';
import { useCanonical } from '../../hooks/useCanonical';
import api from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useAuthStore } from '../../store/authStore';

interface Mission {
  id: string;
  name: string;
  description?: string;
  status: 'planned' | 'in_progress' | 'completed' | 'failed' | 'draft';
  mission_type?: string;
  start_time?: string;
  flight_date?: string;
  area_hectares?: number;
  crop_type?: string;
  created_at: string;
  updated_at: string;
  drone_serial_number?: string;
  pilot_id?: string;
  metadata?: {
    integrity_seal?: string;
    file_count?: number;
    anomaly_id?: string;
    anomaly_type?: string;
    field_name?: string;
    mission_source?: string;
    step?: number;
    [key: string]: any;
  };
}

// ─── Vertical derivation ──────────────────────────────────────────────────────
// Infers which vertical a mission belongs to from mission_type + metadata.
// All AgroTech missions created by the Survey Planner carry metadata.step = 5
// and metadata.anomaly_id.  Manual missions carry metadata.mission_source = 'manual'.

interface VerticalTag {
  label: string;
  shortLabel: string;
  Icon: React.ElementType;
  bg: string;
  text: string;
  darkBg: string;
  darkText: string;
}

const VERTICAL_TAGS: Record<string, VerticalTag> = {
  agrotech: {
    label:      'AgroTech',
    shortLabel: 'AgroTech',
    Icon:       Leaf,
    bg:         'bg-green-100',
    text:       'text-green-800',
    darkBg:     'dark:bg-green-900/30',
    darkText:   'dark:text-green-300',
  },
  oil_gas: {
    label:      'Oil & Gas',
    shortLabel: 'Oil & Gas',
    Icon:       Flame,
    bg:         'bg-orange-100',
    text:       'text-orange-800',
    darkBg:     'dark:bg-orange-900/30',
    darkText:   'dark:text-orange-300',
  },
  infrastructure: {
    label:      'Infrastructure',
    shortLabel: 'Infra',
    Icon:       Building,
    bg:         'bg-slate-100',
    text:       'text-slate-700',
    darkBg:     'dark:bg-slate-800',
    darkText:   'dark:text-slate-300',
  },
  environmental: {
    label:      'Environmental',
    shortLabel: 'Env',
    Icon:       TreePine,
    bg:         'bg-teal-100',
    text:       'text-teal-800',
    darkBg:     'dark:bg-teal-900/30',
    darkText:   'dark:text-teal-300',
  },
  precision: {
    label:      'Precision Planning',
    shortLabel: 'Precision',
    Icon:       Target,
    bg:         'bg-purple-100',
    text:       'text-purple-800',
    darkBg:     'dark:bg-purple-900/30',
    darkText:   'dark:text-purple-300',
  },
  core: {
    label:      'General',
    shortLabel: 'General',
    Icon:       Layers,
    bg:         'bg-gray-100',
    text:       'text-gray-700',
    darkBg:     'dark:bg-gray-800',
    darkText:   'dark:text-gray-300',
  },
};

function deriveVerticalKey(mission: Mission): string {
  const type   = (mission.mission_type ?? '').toLowerCase();
  const name   = (mission.name ?? '').toLowerCase();
  const meta   = mission.metadata ?? {};

  // 1. Explicit vertical stored by manual creation form — most reliable
  if (meta.vertical && VERTICAL_TAGS[meta.vertical]) return meta.vertical;

  // 2. Explicit anomaly linkage from Survey Planner (Step 5)
  if (meta.anomaly_id || meta.step === 5) return 'agrotech';

  // mission_type keywords
  if (type.includes('agro') || type.includes('crop') ||
      type.includes('ndvi') || type.includes('harvest'))       return 'agrotech';
  if (type.includes('drone_survey') || type.includes('thermal_survey') ||
      type.includes('lidar') || type.includes('multispectral') ||
      type.includes('remediation'))                            return 'agrotech';
  if (type.includes('oil') || type.includes('gas') ||
      type.includes('pipeline') || type.includes('methane'))   return 'oil_gas';
  if (type.includes('infra') || type.includes('construction') ||
      type.includes('slope') || type.includes('earthwork'))    return 'infrastructure';
  if (type.includes('env') || type.includes('forest') ||
      type.includes('wetland') || type.includes('carbon'))     return 'environmental';
  if (type.includes('precision') || type.includes('isoxml') ||
      type.includes('variable'))                               return 'precision';

  // Fallback: scan mission name for vertical keywords
  if (name.includes('survey') || name.includes('field') ||
      name.includes('crop') || name.includes('tea') ||
      name.includes('farm') || name.includes('estate'))        return 'agrotech';

  return 'core';
}

function VerticalBadge({ mission }: { mission: Mission }) {
  const key = deriveVerticalKey(mission);
  const tag = VERTICAL_TAGS[key];
  const { Icon, label, bg, text, darkBg, darkText } = tag;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text} ${darkBg} ${darkText}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

interface MissionStats {
  totalMissions: number;
  totalImages: number;
  totalAnalyses: number;
  missions: {
    total: number;
    planned: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
}

export const MissionsListPage: React.FC = () => {
  useCanonical('/missions');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [missions, setMissions] = useState<Mission[]>([]);
  const [stats, setStats] = useState<MissionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter,   setStatusFilter]   = useState<string>('');
  const [verticalFilter, setVerticalFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadMissions();
    loadStats();
  }, [page, statusFilter]);

  const loadMissions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      
      console.log('🔍 Loading missions with params:', params);
      console.log('🔍 Current user:', user);
      
      const response = await api.get('/missions', { params });
      
      console.log('✅ Missions API response:', response.data);
      console.log('✅ Missions array:', response.data.data.missions);
      console.log('✅ Missions count:', response.data.data.missions.length);
      
      setMissions(response.data.data.missions);
      setTotalPages(response.data.data.pagination.pages);
    } catch (err: any) {
      console.error('❌ Error loading missions:', err);
      console.error('❌ Error response:', err.response);
      setError(err.response?.data?.message || 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/missions/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'planned':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Missions
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage and monitor your drone missions
          </p>
        </div>
        <Button
          onClick={() => navigate('/missions/upload')}
          className="flex-shrink-0 bg-primary-600 hover:bg-primary-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Mission Upload
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-100 dark:bg-primary-900 rounded-md p-3">
                <svg className="h-6 w-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Missions</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.missions.total}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 dark:bg-green-900 rounded-md p-3">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.missions.completed}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 rounded-md p-3">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Images</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalImages}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900 rounded-md p-3">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">AI Analyses</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalAnalyses}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="">All Statuses</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="flex-1 min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Vertical
            </label>
            <select
              value={verticalFilter}
              onChange={(e) => { setVerticalFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="">All Verticals</option>
              <option value="agrotech">AgroTech</option>
              <option value="oil_gas">Oil &amp; Gas</option>
              <option value="infrastructure">Infrastructure</option>
              <option value="environmental">Environmental</option>
              <option value="precision">Precision Planning</option>
              <option value="core">General</option>
            </select>
          </div>

          {(statusFilter || verticalFilter) && (
            <Button
              onClick={() => { setStatusFilter(''); setVerticalFilter(''); setPage(1); }}
              variant="outline"
              size="sm"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Missions List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        (() => {
          const filtered = verticalFilter
            ? missions.filter((m) => deriveVerticalKey(m) === verticalFilter)
            : missions;
          return filtered.length === 0 ? (
            <Card className="p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No missions found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {verticalFilter ? 'No missions match the selected vertical.' : 'Get started by creating your first mission.'}
              </p>
              {!verticalFilter && (
                <div className="mt-6">
                  <Button onClick={() => navigate('/missions/upload')}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Mission
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {filtered.map((mission) => (
                <div
                  key={mission.id}
                  onClick={() => navigate(`/missions/${mission.id}`)}
                  className="cursor-pointer"
                >
                  <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {mission.name}
                        </h3>
                        {/* Vertical badge — most important context */}
                        <VerticalBadge mission={mission} />
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(mission.status)}`}>
                          {mission.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {mission.metadata?.integrity_seal && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Sealed
                          </span>
                        )}
                      </div>
                  
                  {mission.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {mission.description}
                    </p>
                  )}
                  
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                    {mission.flight_date && (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(mission.flight_date)}
                      </div>
                    )}
                    
                    {mission.area_hectares && (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        {mission.area_hectares} ha
                      </div>
                    )}
                    
                    {mission.crop_type && (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                        </svg>
                        {mission.crop_type}
                      </div>
                    )}
                    
                    {mission.drone_serial_number && (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        {mission.drone_serial_number}
                      </div>
                    )}
                    
                    {mission.metadata?.file_count && (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {mission.metadata.file_count} files
                      </div>
                    )}
                    {/* Field name from manual missions */}
                    {mission.metadata?.field_name && (
                      <div className="flex items-center gap-1">
                        <Leaf className="w-3.5 h-3.5" />
                        {mission.metadata.field_name}
                      </div>
                    )}
                    {/* Anomaly linkage from Survey Planner */}
                    {mission.metadata?.anomaly_type && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs">⚠ {mission.metadata.anomaly_type}</span>
                      </div>
                    )}
                  </div>
                  </div>

                  <div className="ml-4 flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
                </Card>
              </div>
            ))}
          </div>
         );
       })()
     )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            variant="outline"
          >
            Previous
          </Button>
          
          <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
            Page {page} of {totalPages}
          </span>
          
          <Button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

// Made with Bob
